import { useState } from 'react';
import type { ServerSignals } from '@fraudeai/schema';

/**
 * Карточка адреса — то, ради чего чекеры открывают в первую очередь.
 *
 * Адрес и раньше был в отчёте, но лежал в служебной панели ниже находок, и
 * до него доходили не все. Между тем это первое, что человек хочет увидеть:
 * с какого адреса его видит сайт, кому этот адрес принадлежит и не числится
 * ли он где-нибудь.
 *
 * Подробности — резолверы, открытые порты, отпечатки соединения — остаются
 * ниже. Здесь только то, что отвечает на вопрос «как я выгляжу снаружи»
 * одним взглядом.
 */

const KIND_LABELS: Record<string, string> = {
  vpn: 'сеть VPN-провайдера',
  hosting: 'дата-центр',
  mobile: 'мобильный оператор',
  isp: 'оператор связи',
  education: 'учебная сеть',
  unknown: 'тип не определён',
};

/** Сети, из которых приходят обычные домашние пользователи. */
const RESIDENTIAL = ['isp', 'mobile', 'education'];

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      className="copy"
      onClick={() => {
        // Отсутствие буфера обмена — не повод ломать страницу: на
        // незащищённом соединении и в старых браузерах его просто нет.
        void navigator.clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {});
      }}
    >
      {copied ? 'скопировано' : 'копировать'}
    </button>
  );
}

export function AddressCard({ server }: { server: ServerSignals }) {
  const client = server.reputation?.client;
  const ip = server.remoteIp;
  if (!ip) return null;

  const kind = client?.kind ?? 'unknown';
  // Подозрительным считается всё, откуда обычные люди не приходят. Тип, не
  // определившийся вовсе, к подозрительным не относится: незнание — не улика.
  const suspicious = client != null && !RESIDENTIAL.includes(kind) && kind !== 'unknown';

  const blocklists = client?.blocklists ?? [];
  // Адрес, с которого вышел WebRTC. Показываем только когда он отличается:
  // совпадение — обычное дело и внимания не требует.
  const webrtc = server.probe?.stun?.ip;
  const webrtcDiffers = webrtc != null && webrtc !== ip;

  return (
    <section className="address-card">
      <div className="address-main">
        <div>
          <span className="address-label">Ваш адрес</span>
          <span className="address-ip">{ip}</span>
        </div>
        <CopyButton value={ip} />
      </div>

      <dl className="address-rows">
        {client?.asName && (
          <>
            <dt>Владелец сети</dt>
            <dd>
              AS{client.asn} {client.asName}
            </dd>
          </>
        )}
        {client?.country && (
          <>
            <dt>Страна регистрации</dt>
            <dd>{client.country}</dd>
          </>
        )}
        <dt>Тип сети</dt>
        <dd className={suspicious ? 'bad' : undefined}>{KIND_LABELS[kind] ?? kind}</dd>

        {client?.tor && (
          <>
            <dt>Узел Tor</dt>
            <dd className="bad">да</dd>
          </>
        )}
        {blocklists.length > 0 && (
          <>
            <dt>Чёрные списки</dt>
            <dd className="bad">{blocklists.join(', ')}</dd>
          </>
        )}
        {webrtcDiffers && (
          <>
            <dt>Адрес по UDP</dt>
            <dd className="bad">{webrtc}</dd>
          </>
        )}
      </dl>

      {server.reputation && !server.reputation.available && (
        <p className="hint">
          Базы принадлежности адресов не загружены, поэтому тип сети и чёрные списки не
          проверялись.
        </p>
      )}
    </section>
  );
}
